import { ChangeEvent, FC, useCallback } from 'react';

import './Fields.css';
import clsx from 'clsx';

import { givingAmountMax } from '../../shared/constants';
import { FieldValidationMessage } from '../fieldValidationMessage/FieldValidationMessage';
import { FormControlLabel } from '../label';
import { Textbox, TextboxProps } from '../textbox/textbox';

export type AmountFieldProps = {
	id: string;
	title?: string;
	errorMessage?: string;
	currencyCode?: string;
	currencySymbol?: string;
	classes?: {
		input?: string;
		label?: string;
		validation?: string;
	};
} & TextboxProps;

const DECIMALPLACES = 2;

export const AmountField: FC<AmountFieldProps> = ({
	id,
	title,
	classes,
	invalid,
	step = '0.01',
	currencyCode,
	currencySymbol,
	errorMessage,
	readOnly,
	acceptanceTestTargetId = id,
	required,
	onChange,
	...rest
}) => {
	const handleDecimals = useCallback(
		(e: ChangeEvent<HTMLInputElement>) => {
			if (e && onChange) {
				if (e.currentTarget.value === '') {
					return;
				}
				e.currentTarget.value = Number(e.currentTarget.value).toFixed(DECIMALPLACES);
				onChange(e);
			}
		},
		[onChange]
	);

	const handleInput = useCallback((e: React.FormEvent<HTMLInputElement>) => {
		const input = e.currentTarget;
		let { value } = input;

		value = value
			.replace(/[^\d.]/g, '') // Digits only
			.replace(/^\.+/g, '') // Remove leading dots
			.replace(/\.+/g, '.') // Merge consecutive dots
			.replace(/(\.\d*)\./g, '$1') // Remove extra dots
			.replace(/^0+(?=\d)/, ''); // Remove leading zeros

		const [integerPart, decimalPart] = value.split('.');
		let formatted = integerPart.slice(0, `${givingAmountMax}`.length);

		if (decimalPart !== undefined) {
			formatted += `.${decimalPart.slice(0, 2)}`;
		}

		input.value = formatted;
	}, []);

	return (
		<FormControlLabel
			acceptanceTestTargetId={acceptanceTestTargetId ? `${acceptanceTestTargetId}-label` : undefined}
			className={classes?.label}
			formControlId={id}
			required={required}
			title={title}
		>
			<div className={clsx('amount-field', { 'readonly-field': readOnly })}>
				{currencySymbol && <span className="amount-field-prefix">{currencySymbol}</span>}
				{currencyCode && <span className="amount-field-suffix">{currencyCode}</span>}
				<Textbox
					{...rest}
					acceptanceTestTargetId={acceptanceTestTargetId}
					className={clsx(classes?.input, 'amount-field-input', invalid && 'form-control-invalid')}
					id={id}
					inputMode="decimal"
					invalid={invalid}
					placeholder="0.00"
					readOnly={readOnly}
					step={step}
					type="text"
					onBlur={e => handleDecimals(e)}
					onChange={onChange}
					onInput={handleInput}
				/>
			</div>
			<FieldValidationMessage
				acceptanceTestTargetId={acceptanceTestTargetId ? `${acceptanceTestTargetId}-message` : undefined}
				className={clsx(classes?.validation, invalid && 'field-error')}
				errorMessage={errorMessage}
				id={`${id}-message`}
			/>
		</FormControlLabel>
	);
};
